import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import packageJson from '@/../package.json';

import type {
  ErrorContext,
  InstrumentationProvider,
  LogLevel,
  ProviderConfig,
  ReactErrorHandler,
  UserInfo,
} from '../types';

const SENTRY_DSN =
  'https://7de3d95427519a710ab81998bd490f88@o4505430811869184.ingest.us.sentry.io/4510385055465473';

/**
 * Sentry instrumentation provider for error tracking and monitoring
 */
export class SentryProvider implements InstrumentationProvider {
  name = 'sentry';
  private initialized = false;

  init(config: ProviderConfig): void {
    if (!config.enabled || this.initialized) {
      return;
    }

    Sentry.init({
      dsn: SENTRY_DSN,
      environment: config.environment,
      release: config.release ?? `student-app@${packageJson.version}`,

      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],

      maxBreadcrumbs: 5,

      beforeSend(event, hint) {
        const error = hint.originalException;

        // Filter out browser extension errors
        if (
          event.exception?.values?.[0]?.stacktrace?.frames?.some(frame =>
            frame.filename?.includes('extensions/')
          )
        ) {
          return null;
        }

        // Filter out network errors we can't control
        if (
          error instanceof Error &&
          error.message.includes('Network request failed')
        ) {
          return null;
        }

        return event;
      },

      beforeBreadcrumb(breadcrumb) {
        // Filter out noisy console logs
        if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
          return null;
        }
        return breadcrumb;
      },

      initialScope: {
        tags: {
          app: 'student',
          platform: 'web',
        },
      },
    });

    this.initialized = true;
  }

  captureException(error: Error, context?: ErrorContext): void {
    if (!this.initialized) return;

    Sentry.captureException(error, {
      extra: context?.errorInfo ? { errorInfo: context.errorInfo } : undefined,
      tags: context?.tags,
    });
  }

  captureMessage(message: string, level: LogLevel = 'info'): void {
    if (!this.initialized) return;

    Sentry.captureMessage(message, level);
  }

  setUser(user: UserInfo | null): void {
    if (!this.initialized) return;

    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
    } else {
      Sentry.setUser(null);
    }
  }

  setContext(key: string, value: Record<string, unknown>): void {
    if (!this.initialized) return;

    Sentry.setContext(key, value);
  }

  addBreadcrumb(breadcrumb: {
    category: string;
    message: string;
    level?: LogLevel;
    data?: Record<string, unknown>;
  }): void {
    if (!this.initialized) return;

    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level,
      data: breadcrumb.data,
    });
  }

  getReactErrorHandler(): ReactErrorHandler {
    if (!this.initialized) {
      return () => {
        // No-op when not initialized
      };
    }

    const sentryHandler = Sentry.reactErrorHandler();
    return (error, errorInfo) => {
      sentryHandler(error, errorInfo);
    };
  }
}

export const sentryProvider = new SentryProvider();
